import { useReduxState } from 'hooks';
import {
  ContentLabel,
  ContentValue,
  DisplayFormattedDate,
  FlexContainer,
  User
} from 'components/common';
import { Box } from 'components/common/box';
import { EditorCourseEvaluationDisplaySuggestions } from '.';

import type { GetCourseQualityEvaluationRes } from 'shared-types/responses';
import { Icon } from 'components/common/icons';

interface Props {
  evaluation: GetCourseQualityEvaluationRes['evaluation'];
  suggestions: GetCourseQualityEvaluationRes['suggestions'];
  updateData: () => Promise<void>;
}

const EditorCourseEvaluationDisplay = ({
  evaluation,
  suggestions,
  updateData
}: Props) => {
  const { messages, theme } = useReduxState();

  const hasSuggestions = Object.values(suggestions).some(
    (suggestion) => suggestion.length > 0
  );

  return (
    <div className="wrapper">
      <Box title={messages.commonOverview}>
        <ContentLabel
          horizontal={false}
          label={messages.commonSummary}
          spacing={30}
        >
          <ContentValue
            icon={['far', 'face-grin']}
            iconStyle={{ color: theme.mainColor, marginRight: 10 }}
          >
            {evaluation.summary}
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={false}
          label={messages.courseEvaluationStrengths}
          spacing={30}
        >
          <ContentValue>
            <FlexContainer
              style={{
                alignItems: 'flex-start',
                flexDirection: 'column',
                flexWrap: 'wrap',
                gap: 10
              }}
            >
              {evaluation.strengths.map((strength) => (
                <FlexContainer
                  key={strength}
                  style={{ alignItems: 'flex-start', gap: 10 }}
                >
                  <Icon
                    icon="star"
                    fixedWidth={true}
                    size="sm"
                    style={{ color: theme.mainColor, marginTop: 2 }}
                  />
                  {strength}
                </FlexContainer>
              ))}
            </FlexContainer>
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          horizontalSpacing={30}
          label={messages.courseEvaluationCreatedOn}
          spacing={50}
        >
          <ContentValue>
            <DisplayFormattedDate date={evaluation.evaluatedAt} />
          </ContentValue>
        </ContentLabel>

        <ContentLabel
          horizontal={true}
          horizontalSpacing={30}
          label={messages.courseEvaluationCreatedBy}
          spacing={15}
        >
          <ContentValue>
            {evaluation.createdBy ? (
              <User user={evaluation.createdBy || null} />
            ) : (
              messages.activitiesTextUnknownUser
            )}
          </ContentValue>
        </ContentLabel>
      </Box>

      {hasSuggestions && (
        <div className="suggestions">
          {suggestions.critical.length > 0 && (
            <EditorCourseEvaluationDisplaySuggestions
              evaluationId={evaluation.id}
              severity="critical"
              suggestions={suggestions.critical}
              updateData={updateData}
            />
          )}

          {suggestions.recommended.length > 0 && (
            <EditorCourseEvaluationDisplaySuggestions
              evaluationId={evaluation.id}
              severity="recommended"
              suggestions={suggestions.recommended}
              updateData={updateData}
            />
          )}

          {suggestions.optional.length > 0 && (
            <EditorCourseEvaluationDisplaySuggestions
              evaluationId={evaluation.id}
              severity="optional"
              suggestions={suggestions.optional}
              updateData={updateData}
            />
          )}
        </div>
      )}

      <style jsx>{`
        .suggestions {
          margin-top: 40px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
      `}</style>
    </div>
  );
};

export default EditorCourseEvaluationDisplay;
